import * as THREE from 'three';

/**
 * Ground plane, optionally deformed by an elevation function.
 * Sea is rendered separately by sea.js using OSM coastline data.
 *
 * @param {number}   size    World size in metres (default 3000)
 * @param {Function} elevFn  Optional: elevFn(x, z) → metres above scene centre
 * @returns {THREE.Mesh}
 */
export function createTerrain(size = 3000, elevFn = null) {
  const segs = 60; // more segments than before for smoother elevation
  const geo  = new THREE.PlaneGeometry(size, size, segs, segs);
  geo.rotateX(-Math.PI / 2);

  if (elevFn) {
    const pos = geo.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      pos.setY(i, elevFn(pos.getX(i), pos.getZ(i)));
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
  }

  const mat  = new THREE.MeshStandardMaterial({
    map:       _makeTerrainTexture(),
    roughness: 0.98,
    metalness: 0.0,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.receiveShadow = true;
  mesh.position.y    = -0.06;
  return mesh;
}

// ── Procedural grass/dirt noise texture ───────────────────────────────────────

function _makeTerrainTexture() {
  const SIZE = 256;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = SIZE;
  const ctx = canvas.getContext('2d');

  const imageData = ctx.createImageData(SIZE, SIZE);
  const data = imageData.data;

  // Simple 2-D value noise (no external lib needed)
  function hash2(x, y) {
    const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
    return n - Math.floor(n);
  }
  function valueNoise(x, y) {
    const ix = Math.floor(x), iy = Math.floor(y);
    const fx = x - ix,        fy = y - iy;
    const ux = fx * fx * (3 - 2 * fx);
    const uy = fy * fy * (3 - 2 * fy);
    return (
      hash2(ix,     iy)     * (1 - ux) * (1 - uy) +
      hash2(ix + 1, iy)     * ux       * (1 - uy) +
      hash2(ix,     iy + 1) * (1 - ux) * uy       +
      hash2(ix + 1, iy + 1) * ux       * uy
    );
  }

  // Colour keyframes: dark green → base green → light green / brown patch
  const C = [
    { n: 0.30, r: 52,  g: 96,  b: 60  },  // dark green
    { n: 0.55, r: 74,  g: 124, b: 89  },  // base green
    { n: 0.72, r: 96,  g: 148, b: 96  },  // light green
    { n: 1.00, r: 112, g: 104, b: 72  },  // dry/brown patch
  ];

  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const nx = x / SIZE;
      const ny = y / SIZE;
      // Multi-octave noise
      const n =
        valueNoise(nx *  8, ny *  8) * 0.50 +
        valueNoise(nx * 17, ny * 17) * 0.30 +
        valueNoise(nx * 35, ny * 35) * 0.20;

      // Remap to colour palette
      let r = C[0].r, g = C[0].g, b = C[0].b;
      for (let k = 0; k < C.length - 1; k++) {
        if (n >= C[k].n && n <= C[k + 1].n) {
          const t = (n - C[k].n) / (C[k + 1].n - C[k].n);
          r = C[k].r + (C[k + 1].r - C[k].r) * t;
          g = C[k].g + (C[k + 1].g - C[k].g) * t;
          b = C[k].b + (C[k + 1].b - C[k].b) * t;
          break;
        }
      }

      const i = (y * SIZE + x) * 4;
      data[i]     = r | 0;
      data[i + 1] = g | 0;
      data[i + 2] = b | 0;
      data[i + 3] = 255;
    }
  }

  ctx.putImageData(imageData, 0, 0);

  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(28, 28); // tile 28× across 3000 m → ~107 m per tile
  return tex;
}
